import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Reports|File Imports|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

type LocNameStart = 'SPReportsFileImports';
export type SPReportsFileImportsLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ContentElapsedNA`
	| `${LocNameStart}ContentElapsedTiny`
	| `${LocNameStart}LabelSource`
	| `${LocNameStart}LabelImported`
	| `${LocNameStart}LabelFilename`
	| `${LocNameStart}LabelRecords`
	| `${LocNameStart}LabelElapsed`
	| `${LocNameStart}LabelMessage`
	| `${LocNameStart}LabelSearchKey`
	| `${LocNameStart}LabelDate`
	| `${LocNameStart}LabelFilenameField`
	| `${LocNameStart}ErrorRequired`
	| `${LocNameStart}ErrorInvalidDate`
	| `${LocNameStart}ErrorMaxDate`
	| `${LocNameStart}ButtonSearch`
	| `${LocNameStart}ContentInstructions`;

export const spReportsFileImportsLocs: LocDefs<SPReportsFileImportsLocNames> = {
	SPReportsFileImportsTitleTab: {
		defaultValue: 'File Imports',
		description: 'Tab Title',
		displayPath: `${pathStart}Tab Title`,
		displayType: 'Title',
		legacyName: 'Core/Reporting.FileImports.Title.TabTitle',
		...sharedProperties,
	},
	SPReportsFileImportsTitlePage: {
		defaultValue: 'File Imports',
		description: 'Page Title',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Core/Reporting.FileImports.Title.PageTitle',
		...sharedProperties,
	},
	SPReportsFileImportsContentElapsedNA: {
		defaultValue: 'N/A',
		description: 'Elapsed time not available',
		displayPath: `${pathStart}Elapsed Time NA`,
		displayType: 'Content',
		legacyName: 'Core/Reporting.FileImports.Content.ElapsedNA',
		...sharedProperties,
	},
	SPReportsFileImportsContentElapsedTiny: {
		defaultValue: '< 1',
		description: 'Elapsed time less than 1 second',
		displayPath: `${pathStart}Elapsed Time Tiny`,
		displayType: 'Content',
		legacyName: 'Core/Reporting.FileImports.Content.ElapsedTiny',
		...sharedProperties,
	},
	SPReportsFileImportsLabelSource: {
		defaultValue: 'Source',
		description: 'Label for Source',
		displayPath: `${pathStart}Source`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Source',
		...sharedProperties,
	},
	SPReportsFileImportsLabelImported: {
		defaultValue: 'Imported',
		description: 'Label for Imported',
		displayPath: `${pathStart}Imported`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Imported',
		...sharedProperties,
	},
	SPReportsFileImportsLabelFilename: {
		defaultValue: 'Filename',
		description: 'Label for Filename',
		displayPath: `${pathStart}Filename`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Filename',
		...sharedProperties,
	},
	SPReportsFileImportsLabelRecords: {
		defaultValue: 'Records',
		description: 'Label for Records',
		displayPath: `${pathStart}Records`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Records',
		...sharedProperties,
	},
	SPReportsFileImportsLabelElapsed: {
		defaultValue: 'Elapsed Time (in seconds)',
		description: 'Label for Elapsed Time',
		displayPath: `${pathStart}Elapsed Time`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Elapsed',
		...sharedProperties,
	},
	SPReportsFileImportsLabelMessage: {
		defaultValue: 'Message',
		description: 'Label for Message',
		displayPath: `${pathStart}Message`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.ColumnHeading.Message',
		...sharedProperties,
	},
	SPReportsFileImportsLabelSearchKey: {
		defaultValue: 'Search On',
		description: 'Label for search key dropdown',
		displayPath: `${pathStart}Search Key`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.FieldLabel.SearchKey',
		...sharedProperties,
	},
	SPReportsFileImportsLabelDate: {
		defaultValue: 'Date',
		description: 'Label for date field',
		displayPath: `${pathStart}Date`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.FieldLabel.Date',
		...sharedProperties,
	},
	SPReportsFileImportsLabelFilenameField: {
		defaultValue: 'Filename',
		description: 'Label for filename field',
		displayPath: `${pathStart}Filename`,
		displayType: 'Label',
		legacyName: 'Core/Reporting.FileImports.FieldLabel.Filename',
		...sharedProperties,
	},
	SPReportsFileImportsErrorRequired: {
		defaultValue: 'A search value is required',
		description: 'Error message for required field',
		displayPath: `${pathStart}Required Field Error`,
		displayType: 'Error',
		legacyName: 'Core/Reporting.FileImports.FieldError.Required',
		...sharedProperties,
	},
	SPReportsFileImportsErrorInvalidDate: {
		defaultValue: 'Invalid date',
		description: 'Error message for invalid date',
		displayPath: `${pathStart}Invalid Date Error`,
		displayType: 'Error',
		legacyName: 'Core/Reporting.FileImports.FieldError.InvalidDate',
		...sharedProperties,
	},
	SPReportsFileImportsErrorMaxDate: {
		defaultValue: 'Please select a date in the past',
		description: 'Error message for future date',
		displayPath: `${pathStart}Future Date Error`,
		displayType: 'Error',
		legacyName: 'Core/Reporting.FileImports.FieldError.MaxDate',
		...sharedProperties,
	},
	SPReportsFileImportsButtonSearch: {
		defaultValue: 'Search',
		description: 'Label for search button',
		displayPath: `${pathStart}Search Button`,
		displayType: 'Button',
		legacyName: 'Core/Reporting.FileImports.Button.Search',
		...sharedProperties,
	},
	SPReportsFileImportsContentInstructions: {
		defaultValue: 'Use the search to find import files',
		description: 'Instructions for using the search',
		displayPath: `${pathStart}Search Instructions`,
		displayType: 'Content',
		legacyName: 'Core/Reporting.FileImports.Content.Instructions',
		shared: false,
		feature: HubFeature.Core,
		portal: 'SP',
		htmlAllowed: true,
	},
};

export type SPReportsFileImportsLocType = LocMap<SPReportsFileImportsLocNames>;
